import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Zap } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data?.dev_link) {
        setDevLink(data.dev_link);
        toast.success("DEV MODE: reset link shown below");
      } else {
        toast.success("Check your inbox for the reset link");
      }
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-brand-gold text-white p-12">
        <Link to="/" className="flex items-center gap-2" data-testid="forgot-logo">
          <div className="w-8 h-8 bg-card flex items-center justify-center">
            <Zap className="w-4 h-4 text-foreground" />
          </div>
          <span className="font-black text-lg tracking-tight">StenoSync</span>
        </Link>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-white/50 mb-6">Password reset</div>
          <h1 className="text-5xl font-black tracking-tight leading-none">
            Lost your<br />password?<br />
            <span className="text-brand-emerald">We got you.</span>
          </h1>
          <p className="mt-6 max-w-md font-mono text-white/70">
            Enter your registered email and we'll send you a secure reset link. Link expires in 30 minutes.
          </p>
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-bold">HIGH COURT PS training studio</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">Forgot password</div>
          <h2 className="text-4xl font-black tracking-tight mb-2">Reset link</h2>
          <p className="font-mono text-sm text-muted-foreground mb-8">We'll email you a one-time reset link.</p>

          {sent ? (
            <div className="space-y-5" data-testid="forgot-sent-panel">
              <div className="border-l-4 border-green-500 bg-green-50 px-4 py-3 text-sm font-mono text-green-800">
                If an account exists for <b>{email}</b>, a reset link has been sent. Check your inbox (and spam folder).
              </div>
              {devLink && (
                <div className="border border-border px-4 py-3 text-xs font-mono break-all" data-testid="forgot-dev-link-box">
                  <div className="uppercase tracking-[0.2em] font-bold text-brand-gold mb-1">DEV MODE reset link</div>
                  <a href={devLink} className="underline text-brand-gold" data-testid="forgot-dev-link">{devLink}</a>
                </div>
              )}
              <div className="text-sm font-mono text-muted-foreground">
                <Link to="/login" className="text-brand-gold font-bold underline underline-offset-4" data-testid="forgot-back-to-login">
                  ← Back to log in
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="forgot-form">
              <div className="space-y-5">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-bold">Email</Label>
                  <Input
                    type="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 rounded-lg border-border h-12 font-mono"
                    data-testid="forgot-email-input"
                    placeholder="you@example.com"
                  />
                </div>

                {err && (
                  <div className="border-l-4 border-red-500 bg-red-50 px-4 py-2 text-sm font-mono text-red-700" data-testid="forgot-error">
                    {err}
                  </div>
                )}

                <Button
                  type="submit" disabled={loading}
                  className="w-full rounded-lg h-12 bg-brand-gold hover:bg-brand-gold/90 text-white text-xs uppercase tracking-[0.2em] font-bold transition-transform hover:-translate-y-0.5"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
              </div>

              <div className="mt-8 text-sm font-mono text-muted-foreground">
                Remember your password?{" "}
                <Link to="/login" className="text-brand-gold font-bold underline underline-offset-4" data-testid="forgot-goto-login">
                  Log in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
